import { frameworkTypeLabels } from "@/modules/sessions/constants";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, Radio, RadioGroup, ScrollShadow, Tooltip } from "@nextui-org/react";
import { Check, SearchIcon, SlidersHorizontal } from "lucide-react"; // Icon for search
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import UseAskAiSocketEvents from "../hooks/useAskAiEvents";
import { useChatStore } from "../states";

export default function SessionPopOver() {

  const { selectedSession, setSelectedSession, sessionList, threadId } = useChatStore();
  const { sendSelectedSessions } = UseAskAiSocketEvents();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isPopoverDisabled, setIsPopoverDisabled] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const items = [
    { title: 'All Sessions' },
    { title: 'All Risk Assessments' },
    { title: 'All Gap Assessments' },
    { title: 'All Third-Party Risk Assessments' },
    { title: 'All Security Quesionaire' },
  ]

  // Debounce search input for better performance
  const debounceTimer = useRef<NodeJS.Timeout>();
  
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search]);

  const filteredSessions = useMemo(() => 
    sessionList.filter((session) =>
      session.name.toLowerCase().includes(debouncedSearch.toLowerCase()) || 
      frameworkTypeLabels[session.type]?.toLowerCase().includes(debouncedSearch.toLowerCase())
    ),
    [sessionList, debouncedSearch]
  );

  const handleSessionSelect = useCallback((session: any) => {
    setSelectedSession(session);
  }, [setSelectedSession]);

  const onClose = () => {
    if (selectedSession?.id) {
      sendSelectedSessions(threadId, [selectedSession?.id])
      setIsPopoverDisabled(true)
    };
    setIsPopoverOpen(false)
  }

  return (
    <Popover placement="top-start" onClose={() => onClose()} isOpen={isPopoverOpen} >
      <PopoverTrigger>
        <Button
          disabled={selectedSession?.id ? true : false}
          onClick={() => {
            if (isPopoverDisabled && selectedSession?.id) return;
            else setIsPopoverOpen(true);
          }}
          size="sm"
          startContent={
            <SlidersHorizontal size={18} />
          }
          variant="flat"
        >
          <Tooltip delay={500} showArrow={true}
            classNames={{
              content: "bg-black/95 backdrop-blur-md text-white text-tiny p-3",
              arrow: "backdrop-blur-md",
              base: "before:bg-black/95", // This targets the pseudo-element used for the arrow
            }}
            closeDelay={0}
            content={selectedSession?.id ? `Selection disabled — you cannot change session after the session has begun.` : `Select either all or single session to tailor response`}>
            {selectedSession?.name || 'All'}
          </Tooltip>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[600px] py-3">
        <div className="flex flex-col gap-3 w-full">
          {/* Heading instead of tabs */}
          <div className="w-full px-2 text-left">
            <h3 className="text-medium font-medium">Select session</h3>
          </div>

          <div className="flex flex-col gap-3">
            <Input
              placeholder="Search by session title or type"
              startContent={<SearchIcon size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ScrollShadow className="max-h-64">
              {filteredSessions.map((session) => (
                <div key={session.id} className="p-2 hover:bg-gray-100 cursor-pointer rounded w-full inline-block" onClick={() => handleSessionSelect(session)}>
                  <span>{session.name}</span>
                  <span className="pl-2 text-sm text-gray-500">{frameworkTypeLabels[session.type] || 'N/A'} </span>
                  {
                    selectedSession?.id === session.id ?
                      <Check className="flex float-right mr-4" width={20} />
                      : <></>
                  }
                </div>
              ))}
            </ScrollShadow>
          </div>
        </div>
      </PopoverContent>
    </Popover >
  );
}